import React from 'react';
import { Calendar, Target, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';

export default function FinancialGoalCard({
  title = "Vacation Fund",
  subtitle = "Dream Trip to Japan",
  currentAmount = 3500,
  targetAmount = 10000,
  createdDate = new Date(),
  currency = "$",
  showCompleted = false
}) {
  const progress = Math.min((currentAmount / targetAmount) * 100, 100);
  const remainingAmount = Math.max(targetAmount - currentAmount, 0);
  const isCompleted = progress >= 100;

  // Don't render if goal is completed and showCompleted is false
  if (isCompleted && !showCompleted) {
    return null;
  }

  return (
    <div className={`group relative bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden border ${isCompleted ? 'border-emerald-200' : 'border-gray-100'}`}>
      {/* Decorative gradient background */}
      <div className={`absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl opacity-40 -translate-y-20 translate-x-20 group-hover:scale-110 transition-transform duration-700 ${isCompleted ? 'bg-gradient-to-br from-emerald-50 via-teal-50 to-transparent' : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-transparent'}`} />
      
      <div className="relative p-6 md:p-8">
        {/* Header Section */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-lg ${isCompleted ? 'bg-gradient-to-br from-emerald-500 to-teal-600 shadow-emerald-500/20' : 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-indigo-500/20'}`}>
                <Target className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className={`text-xl md:text-2xl font-bold tracking-tight ${isCompleted ? 'text-emerald-900' : 'text-gray-900'}`}>
                  {title}
                </h3>
              </div>
            </div>
            <p className={`text-sm md:text-base font-medium pl-12 ${isCompleted ? 'text-emerald-600' : 'text-gray-500'}`}>
              {subtitle}
            </p>
          </div>
          
          {/* Percentage Badge */}
          <div className="ml-4 flex flex-col items-end">
            <div className={`px-3 py-1.5 rounded-full shadow-lg ${isCompleted ? 'bg-gradient-to-r from-emerald-500 to-teal-600 shadow-emerald-500/20' : 'bg-gradient-to-r from-indigo-500 to-purple-600 shadow-indigo-500/20'}`}>
              <span className="text-sm font-bold text-white">
                {progress.toFixed(0)}%
              </span>
            </div>
          </div>
        </div>


        {/* Progress Section */}
        <div className="space-y-3 mb-6">
          {/* Amount Display */}
          <div className="flex items-baseline justify-between">
            <div>
              <span className={`text-3xl md:text-4xl font-bold bg-clip-text text-transparent ${isCompleted ? 'bg-gradient-to-r from-emerald-600 to-teal-600' : 'bg-gradient-to-r from-indigo-600 to-purple-600'}`}>
                {currency}{currentAmount.toLocaleString()}
              </span>
              <span className={`text-lg ml-2 ${isCompleted ? 'text-emerald-400' : 'text-gray-400'}`}>
                / {currency}{targetAmount.toLocaleString()}
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`absolute inset-y-0 left-0 rounded-full transition-all duration-1000 ease-out shadow-lg ${isCompleted ? 'bg-gradient-to-r from-emerald-500 via-emerald-400 to-teal-500 shadow-emerald-500/30' : 'bg-gradient-to-r from-indigo-500 via-indigo-400 to-purple-500 shadow-indigo-500/30'}`}
              style={{ width: `${progress}%` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 animate-shimmer" />
            </div>
          </div>

          {/* Remaining Amount */}
          <div className="flex items-center justify-between text-sm">
            <div className={`flex items-center gap-1.5 ${isCompleted ? 'text-emerald-500' : 'text-gray-500'}`}>
              <TrendingUp className="w-4 h-4" />
              <span>
                {isCompleted ? 'Goal completed! 🎉' : `${currency}${remainingAmount.toLocaleString()} remaining`}
              </span>
            </div>
          </div>
        </div>

        {/* Footer - Created Date */}
        <div className={`pt-4 border-t ${isCompleted ? 'border-emerald-100' : 'border-gray-100'}`}>
          <div className={`flex items-center gap-2 text-xs md:text-sm ${isCompleted ? 'text-emerald-400' : 'text-gray-400'}`}>
            <Calendar className="w-4 h-4" />
            <span>Created {format(new Date(createdDate), 'MMMM d, yyyy')}</span>
          </div>
        </div>
      </div>

    </div>
  );
}